import { Global } from '@emotion/react';
import { useGrowthBook } from '@growthbook/growthbook-react';
import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import AuthProvider, { Auth } from 'contexts/Auth';
import { graphql, useStaticQuery } from 'gatsby';
import { useContext, useEffect } from 'react';
import Helmet from 'react-helmet';
import theme, { GlobalStyle } from '../theme';

type MainProps = {
  children: React.ReactNode;
};

function Main({ children }: MainProps): JSX.Element {
  const { isLoading, isAuthenticated, viewer } = useContext(Auth);
  const growthbook = useGrowthBook();

  useEffect(() => {
    if (isLoading) {
      // no point of setting attributes if we are still loading Auth context
      return;
    }

    growthbook?.setAttributes({
      id: viewer?.id,
      loggedIn: isAuthenticated,
      employee: viewer?.employee,
      // TODO @fguerra allow ramping per clients
      // check if we should allow user to choose client first (dropdown) so we have only 1 option here
      // clients: viewer.clients.nodes.map(({id}) => {id}),
    });
  }, [isLoading, isAuthenticated, viewer]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
        <CssBaseline />
        <Global styles={GlobalStyle} />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

type Props = {
  children: React.ReactNode;
};

export default function Layout({ children }: Props): JSX.Element {
  const data = useStaticQuery(graphql`
    query SiteTitleQuery {
      site {
        siteMetadata {
          name
          title
        }
      }
    }
  `);

  return (
    <>
      <Helmet
        titleTemplate={`%s - ${data.site.siteMetadata.name}`}
        defaultTitle={data.site.siteMetadata.title}
        meta={[
          {
            name: 'description',
            content: 'Executive search reimagined',
          },
          {
            name: 'keywords',
            content:
              'inkwell, flexible jobs, work, flexible, flexible work, flexible part time jobs, part time jobs, leader, revolution',
          },
          {
            name: 'google-site-verification',
            content: '27xZkWlGpPw5sKiMlVExvU3mN04a3iWnijgWQmbYsfg',
          },
          {
            name: 'apple-mobile-web-app-status-bar-style',
            content: 'default',
          },
        ]}
      >
        <html lang="en" />
      </Helmet>
      <AuthProvider>
        <Main>{children}</Main>
      </AuthProvider>
    </>
  );
}
